/*
  Payment header displayed when "displayPaymentView" is true
  i.e. active loans that are accepting payments
*/
import { find } from 'lodash';

import { PortalSelectedLoan } from '../../../../App';
import { InterestDueAtPayoffHeader } from './headers/InterestDueAtPayoffHeader';
import { PaidCurrentHeader } from './headers/PaidCurrentHeader';
import { PaymentDueHeader } from './headers/PaymentDueHeader';
import { WorkoutPlanHeader } from './headers/WorkoutPlanHeader';

export interface Props {
  loan: PortalSelectedLoan;
}

export const PaymentHeader = ({ loan }: Props) => {
  // Determine if there there is anything due on the loan
  const {
    currentSnapshot: {
      paymentDueStatus,
      totalAmountRemainingFromBorrower,
      totalUpcomingAmountRemainingFromBorrower,
      payoffs,
      maturityConfig: { isMaturing },
      isFullInterestOnlyLoan,
      interestDueAtPayoff,
      activeWorkoutPlan,
      totalWorkoutPlanAmountRemainingFromBorrower,
    },
  } = loan;

  const hasStatementPaymentDue = paymentDueStatus === 'due';
  const hasBorrowerAmountRemaining = totalAmountRemainingFromBorrower > 0;
  const hasActivePayoff = find(payoffs, ['status', 'pending']);
  const hasUpcomingAmountDue = totalUpcomingAmountRemainingFromBorrower > 0;
  const paymentDueOrMaturing =
    hasStatementPaymentDue ||
    hasUpcomingAmountDue ||
    hasBorrowerAmountRemaining ||
    ((hasActivePayoff || isMaturing) && !isFullInterestOnlyLoan);

  if (
    activeWorkoutPlan &&
    ['ForbearanceWorkoutPlan', 'FlexModificationWorkoutPlan', 'RepaymentWorkoutPlan'].includes(
      activeWorkoutPlan.__typename ?? '',
    )
  ) {
    return (
      <WorkoutPlanHeader
        activeWorkoutPlan={activeWorkoutPlan}
        amountDue={totalWorkoutPlanAmountRemainingFromBorrower ?? 0}
      />
    );
  }

  return (
    <section id="payment-header" aria-label="Payment Due Details" className="text-center mb-4">
      {interestDueAtPayoff ? (
        <InterestDueAtPayoffHeader />
      ) : paymentDueOrMaturing ? (
        <PaymentDueHeader loan={loan} />
      ) : (
        <PaidCurrentHeader loan={loan} />
      )}
    </section>
  );
};
